import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  feedbackTypeLabelZh,
  getFeedback,
  listFeedback,
  updateFeedbackStatus,
  type AdminFeedbackItem,
} from '@/api/feedbackAdmin';

function dateToStartMs(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  return d.getTime();
}

function dateToEndMs(dateStr: string): number {
  const d = new Date(dateStr + 'T23:59:59.999');
  return d.getTime();
}

function formatSubmitTime(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function FeedbackAdminPage() {
  const navigate = useNavigate();
  const [list, setList] = useState<AdminFeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [keyword, setKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [drawerItem, setDrawerItem] = useState<AdminFeedbackItem | null>(null);
  const [remarkModal, setRemarkModal] = useState<{ id: string; action: 'done' } | null>(null);
  const [processRemark, setProcessRemark] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async (page: number) => {
    if (startDate && endDate && dateToStartMs(startDate) > dateToEndMs(endDate)) {
      setError('开始日期不能晚于结束日期');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params: Parameters<typeof listFeedback>[0] = {
        pageNumber: page,
        pageSize,
      };
      if (statusFilter !== '') params.status = Number(statusFilter) as 0 | 1;
      if (keyword.trim()) params.keyword = keyword.trim();
      if (startDate) params.startTime = dateToStartMs(startDate);
      if (endDate) params.endTime = dateToEndMs(endDate);
      const result = await listFeedback(params);
      setList(result.list);
      setTotal(result.total);
      setPageNumber(result.pageNumber);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [endDate, keyword, pageSize, startDate, statusFilter]);

  useEffect(() => {
    fetchList(1);
  }, []);

  const handleSearch = () => fetchList(1);

  const handleReset = async () => {
    setStatusFilter('');
    setKeyword('');
    setStartDate('');
    setEndDate('');
    setError('');
    setLoading(true);
    try {
      const result = await listFeedback({ pageNumber: 1, pageSize });
      setList(result.list);
      setTotal(result.total);
      setPageNumber(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = async (id: string) => {
    try {
      const item = await getFeedback(id);
      setDrawerItem(item);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载详情失败');
    }
  };

  const confirmStatusUpdate = async (id: string, status: 0 | 1, remark?: string) => {
    if (remark && remark.length > 500) {
      setError('处理备注不能超过 500 字符');
      return;
    }
    setActionLoading(true);
    try {
      await updateFeedbackStatus({ id, status, processRemark: remark });
      setRemarkModal(null);
      setProcessRemark('');
      await fetchList(pageNumber);
      if (drawerItem?.id === id) {
        const updated = await getFeedback(id);
        setDrawerItem(updated);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新失败');
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout title="反馈管理" subtitle={`共 ${total} 条反馈`}>
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">状态</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[120px]"
            >
              <option value="">全部</option>
              <option value="0">未处理</option>
              <option value="1">已处理</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">关键词</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="内容 / 资料标题"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[180px]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">开始日期</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">结束日期</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
            style={{ background: '#0069BD' }}
          >
            查询
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 cursor-pointer"
          >
            重置
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="px-4 py-3">资料标题</th>
                  <th className="px-4 py-3">反馈内容</th>
                  <th className="px-4 py-3">类型</th>
                  <th className="px-4 py-3">代理商</th>
                  <th className="px-4 py-3">提交时间</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">加载中…</td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">暂无反馈</td>
                  </tr>
                ) : (
                  list.map((item) => (
                    <tr key={item.id} className="border-t border-gray-50 hover:bg-gray-50/80">
                      <td className="px-4 py-3 max-w-[160px]">
                        <button
                          type="button"
                          className="text-[#0069BD] font-medium hover:underline text-left truncate max-w-full block"
                          onClick={() => navigate(`/admin/materials/${item.materialId}`)}
                        >
                          {item.materialTitle}
                        </button>
                      </td>
                      <td className="px-4 py-3 max-w-[200px] truncate text-gray-600">{item.content}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{feedbackTypeLabelZh(item.feedbackType)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-[#0069BD] hover:underline"
                          onClick={() => navigate(`/admin/agents/${item.agentId}/activity`)}
                        >
                          {item.agentName || item.agentId}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{formatSubmitTime(item.submitTime)}</td>
                      <td className="px-4 py-3">
                        {item.status === 1 ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">已处理</span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">未处理</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button type="button" className="text-xs text-gray-600 hover:text-[#0069BD] cursor-pointer" onClick={() => openDrawer(item.id)}>查看</button>
                          {item.status === 0 ? (
                            <button
                              type="button"
                              className="text-xs text-[#0069BD] cursor-pointer"
                              onClick={() => { setRemarkModal({ id: item.id, action: 'done' }); setProcessRemark(''); }}
                            >
                              标记已处理
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="text-xs text-gray-500 cursor-pointer"
                              onClick={() => {
                                if (window.confirm('确认标记为未处理？')) {
                                  confirmStatusUpdate(item.id, 0);
                                }
                              }}
                            >
                              标记未处理
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
              <span>共 {total} 条</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pageNumber <= 1}
                  onClick={() => fetchList(pageNumber - 1)}
                  className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 cursor-pointer"
                >
                  上一页
                </button>
                <span>{pageNumber} / {totalPages}</span>
                <button
                  type="button"
                  disabled={pageNumber >= totalPages}
                  onClick={() => fetchList(pageNumber + 1)}
                  className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 cursor-pointer"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {drawerItem && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.3)' }} onClick={() => setDrawerItem(null)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">反馈详情</h3>
              <button type="button" onClick={() => setDrawerItem(null)} className="text-gray-400 hover:text-gray-700 cursor-pointer">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 mb-1">资料</p>
                <p className="font-semibold">{drawerItem.materialTitle}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">代理商</p>
                <p>{drawerItem.agentName || drawerItem.agentId}</p>
                {drawerItem.agentEmail && <p className="text-gray-500 text-xs mt-0.5">{drawerItem.agentEmail}</p>}
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">类型</p>
                <p>{feedbackTypeLabelZh(drawerItem.feedbackType)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">反馈内容</p>
                <p className="whitespace-pre-wrap text-gray-700">{drawerItem.content}</p>
              </div>
              {drawerItem.processRemark && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                  <p className="text-xs text-green-700 font-semibold mb-1">处理备注</p>
                  <p className="text-green-800 whitespace-pre-wrap">{drawerItem.processRemark}</p>
                  {drawerItem.processByName && (
                    <p className="text-xs text-green-600 mt-2">处理人：{drawerItem.processByName}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {remarkModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl">
            <h3 className="font-bold text-gray-900 mb-3">标记已处理</h3>
            <label className="block text-xs font-semibold text-gray-500 mb-1">处理备注（可选）</label>
            <textarea
              className="w-full min-h-[80px] border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4"
              value={processRemark}
              onChange={(e) => setProcessRemark(e.target.value)}
              placeholder="填写处理说明…"
              maxLength={500}
            />
            <p className="text-xs text-gray-400 -mt-2 mb-4 text-right">{processRemark.length}/500</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRemarkModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg cursor-pointer">取消</button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => confirmStatusUpdate(remarkModal.id, 1, processRemark.trim() || undefined)}
                className="px-4 py-2 text-sm text-white rounded-lg cursor-pointer disabled:opacity-50"
                style={{ background: '#0069BD' }}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
